/**
 * RinggWidgetController — the composition root of the widget brain.
 *
 * Owns every store, wires transport events into them, and exposes the
 * imperative API the platform UIs call. This is the headless replacement for
 * the production widget's App.tsx orchestration: platform bindings render
 * snapshots and forward user intents; ALL behavior lives here or in the
 * stores.
 */
import { createRinggApiClient } from "./api/client.js";
import { createSystemClock } from "./ports/clock.js";
import { grantedMicPermission, silentNotificationPlayer } from "./ports/platform.js";
import { createCallbackEventBus } from "./ports/event-bus.js";
import { createMessageStore } from "./stores/message-store.js";
import { createTypingStore } from "./stores/typing-store.js";
import { createComponentStore } from "./stores/component-store.js";
import { createSlashCommandStore } from "./stores/slash-command-store.js";
import { createSessionStore } from "./stores/session-store.js";
import { createShellStore } from "./stores/shell-store.js";
import { mergeWidgetTheme } from "./theme/theme-engine.js";
import { transformRpcToComponent, formatComponentResponse, formatSlashCommand, formatBlocksAction, formatDynamicData } from "./rpc/formatter.js";
import { DEFAULT_CONFIG } from "./types/config.js";
export function createRinggWidgetController(config, ports) {
    const theme = mergeWidgetTheme(config.theme);
    const eventBus = ports.eventBus ?? createCallbackEventBus();
    const clock = ports.clock ?? createSystemClock();
    const notification = ports.notification ?? silentNotificationPlayer;
    const onDomAction = ports.onDomAction;
    const transport = ports.transport;
    const api = createRinggApiClient({
        urlResolver: ports.urlResolver,
        mode: config.mode ?? DEFAULT_CONFIG.mode,
        xApiKey: config.xApiKey,
        authorization: config.authorization,
        clientOrigin: config.clientOrigin,
    });
    const shell = createShellStore(eventBus, config.defaultTab ?? DEFAULT_CONFIG.defaultTab, config.defaultExpanded ?? DEFAULT_CONFIG.defaultExpanded);
    const session = createSessionStore(api, transport, ports.micPermission ?? grantedMicPermission);
    const messages = createMessageStore(clock);
    const typing = createTypingStore(clock);
    const slashCommands = createSlashCommandStore(config.enabledSlashCommands);
    const components = createComponentStore(clock, (component) => commitComponent(component));
    const unsubscribers = [];
    // ParticipantDisconnected AND Disconnected both funnel into endCall (the
    // transport's onSessionEnded fires for either, and livekit emits Disconnected
    // synchronously inside room.disconnect() while endCall is still awaiting), so
    // the "ended" host event needs the same once-per-conversation guard as
    // production's conversationEndedDispatched ref (App.tsx:259-264).
    let conversationEndedDispatched = false;
    // Inline chat pills for DOM-action/system events (App.tsx:945-967). Off by
    // default; `eventLogs.showIds` surfaces the action id next to the meta.
    const eventLogsEnabled = config.eventLogs?.enabled === true;
    const eventLogsShowIds = config.eventLogs?.showIds === true;
    const pushSystemLog = (entry) => {
        if (!eventLogsEnabled)
            return;
        const meta = eventLogsShowIds && entry.id ? (entry.meta ? `${entry.id} · ${entry.meta}` : entry.id) : entry.meta;
        messages.addSystemLog({ label: entry.label, meta, level: entry.level });
    };
    // ─── Internal wiring ───────────────────────────────────────────────────────
    function resetConversation() {
        messages.reset();
        components.reset();
    }
    function commitComponent(component) {
        messages.addComponent(component);
        eventBus.emit("ringg:calendar_booking", { status: "shown", componentId: component.component_id });
        // A rendered component *is* the agent's reply — the turn passes to the
        // user, so drop the dots.
        typing.clear();
        if (!shell.isOpen)
            notification.play();
        shell.open();
    }
    async function handleAgentTextStream(stream) {
        // Production registers this handler only while callMode === "text" &&
        // isSessionLive (App.tsx:1129). LiveKit Agents backends publish agent/user
        // utterances on BOTH the legacy transcription packets and this topic, so
        // committing these streams during an audio call would render every
        // utterance twice (live upsert + chat bubble).
        if (shell.getSnapshot().callMode !== "text" || !session.isSessionLive)
            return;
        try {
            const message = await stream.readAll();
            if (!stream.isAgent) {
                messages.addChatMessage({ message, isSelf: true, name: "You" });
                // Any landed text-stream message consumes the pending RAG URL —
                // production clears the ref unconditionally (App.tsx:1159).
                messages.clearPendingSourceUrl();
                return;
            }
            // Hold the dots up for at least the minimum duration even if the reply
            // came back fast — otherwise the indicator just flashes.
            await typing.waitMinDuration();
            if (!shell.isOpen)
                notification.play();
            shell.open();
            messages.addChatMessage({ message, isSelf: false, name: "Agent" });
            // Any landed reply clears the dots, unconditionally.
            typing.clear();
            // Release any widget buffered for this turn so it sorts directly below.
            components.onAgentTextLanded();
        }
        catch {
            if (stream.isAgent)
                typing.clear();
        }
    }
    async function handleBlocksStream(stream) {
        try {
            const raw = await stream.readAll();
            typing.clear();
            const parsed = JSON.parse(raw);
            if (parsed.component_type !== "render_component_blocks" || !Array.isArray(parsed.component_config?.blocks))
                return;
            components.add({
                component_type: "blocks",
                component_id: `blocks_${clock.now()}`,
                data: parsed.component_config,
            }, shell.getSnapshot().callMode);
        }
        catch {
            // Malformed blocks payloads are dropped; the conversation continues.
        }
    }
    async function handleDynamicData(invocation) {
        typing.clear();
        let parsed;
        try {
            parsed = JSON.parse(invocation.payload);
        }
        catch {
            throw new Error("Invalid send_dynamic_data payload");
        }
        const componentType = parsed.component_type;
        if (!componentType) {
            return JSON.stringify({ status: "error", message: "No component_type provided" });
        }
        // Fire-and-forget host-page action — execute and ack immediately.
        // Backend payload uses { action_id, event_name, detail }; normalize to the
        // internal shape exactly like production (App.tsx:986-995): kind defaults
        // to "trigger_event" when absent.
        if (componentType === "execute_dom_action") {
            const cfg = (parsed.component_config ?? parsed.component_data ?? {});
            const action = {
                id: cfg.id ?? cfg.action_id ?? "",
                kind: cfg.kind ?? "trigger_event",
                event_name: cfg.event_name,
                default_payload: cfg.default_payload ?? cfg.detail,
            };
            onDomAction?.(action, pushSystemLog);
            return JSON.stringify({ status: "received" });
        }
        const componentName = componentType.replace("render_component_", "");
        // Client packs get first refusal (e.g. disease_search_results).
        if (components.runExtensions(componentName, parsed)) {
            return JSON.stringify({ status: "received" });
        }
        const component = transformRpcToComponent(componentName, parsed);
        if (!component) {
            return JSON.stringify({ status: "error", message: `Unknown component type: ${componentName}` });
        }
        components.trackPendingResponse(component.component_id, {
            name: componentName,
            originalConfig: parsed.component_config ?? parsed.component_data,
        });
        components.add(component, shell.getSnapshot().callMode);
        return JSON.stringify({ status: "received", component_id: component.component_id });
    }
    /**
     * Legacy `render_component` RPC (production's componentRpcHandler,
     * App.tsx:789-805): the payload is already in the internal component format
     * — commit it untouched when component_type && component_id are present.
     * No transform, no pending-response tracking, and a bare {status:"received"}
     * ack (never a component_id).
     */
    async function handleRenderComponent(invocation) {
        typing.clear();
        let parsed;
        try {
            parsed = JSON.parse(invocation.payload);
        }
        catch {
            throw new Error("Invalid component payload");
        }
        if (parsed.component_type && parsed.component_id) {
            components.add(parsed, shell.getSnapshot().callMode);
        }
        return JSON.stringify({ status: "received" });
    }
    function wireTransport() {
        transport.registerRpcMethod("send_dynamic_data", (invocation) => handleDynamicData(invocation));
        // Legacy method name — pre-formatted payloads, committed as-is.
        transport.registerRpcMethod("render_component", (invocation) => handleRenderComponent(invocation));
        // RAG citations (App.tsx:775-786): `source_urls` array → ALL urls joined
        // with ", "; else the singular `source_url` string. Other keys are ignored
        // (production never sends them) and malformed JSON rejects the RPC.
        transport.registerRpcMethod("send_source_urls", async (invocation) => {
            let parsed;
            try {
                parsed = JSON.parse(invocation.payload);
            }
            catch {
                throw new Error("Invalid payload for send_source_urls");
            }
            if (Array.isArray(parsed.source_urls))
                messages.setPendingSourceUrl(parsed.source_urls.join(", "));
            else if (parsed.source_url)
                messages.setPendingSourceUrl(String(parsed.source_url));
            return JSON.stringify({ status: "received" });
        });
        // Agent text replies (text mode). Registered up front — text streams
        // aren't replayed, so registering only after "connected" can drop the
        // agent's greeting.
        transport.registerTextStreamHandler("lk.transcription", (stream) => {
            void handleAgentTextStream(stream);
        });
        // Block Kit arrives over a text stream (chunked → no RPC size cap).
        transport.registerTextStreamHandler("ringg.blocks", (stream) => {
            void handleBlocksStream(stream);
        });
        unsubscribers.push(transport.onChatMessage((message) => {
            // Local sends are committed optimistically in sendMessage() — an
            // adapter echoing them anyway must not duplicate the bubble.
            if (message.isSelf)
                return;
            // Incoming chat commits only in text mode (production merges
            // `chatMessages` inside the callMode === "text" && isSessionLive
            // effect only — App.tsx:1129, 1187-1206).
            if (shell.getSnapshot().callMode !== "text" || !session.isSessionLive)
                return;
            messages.addChatMessage({ message: message.message, isSelf: false, name: message.senderName });
            if (message.isAgent)
                typing.clear();
        }), transport.onTranscription((segment) => {
            // Transcription packets merge only during a connected audio call
            // (App.tsx:1074) — text mode renders the lk.transcription streams
            // instead, and both channels carry the same utterances.
            const { callMode } = shell.getSnapshot();
            if (callMode !== "audio" || session.getSnapshot().connectionState !== "connected")
                return;
            messages.upsertTranscription(segment);
        }), transport.onSessionEnded(() => {
            // Remote hangup / room drop — same path as the user ending the call.
            if (session.isSessionLive)
                void controller.endCall();
        }));
    }
    const controller = {
        config,
        theme,
        eventBus,
        shell,
        session,
        messages,
        typing,
        components,
        slashCommands,
        // ─── Widget shell ──────────────────────────────────────────────────────────
        openWidget() {
            shell.open();
            // Opening is a clear intent signal — warm the connection while the user
            // reads the start screen (never on page views where it's never opened).
            if (!session.isSessionLive)
                session.prewarm();
        },
        minimizeWidget() {
            shell.minimize();
        },
        handleTriggerClick() {
            // defaultExpanded renders no trigger at all — a stray click must never
            // toggle the panel or start a call (the widget is permanently open).
            if (config.defaultExpanded)
                return;
            const { isLoading, connectionState } = session.getSnapshot();
            if (config.bypassStartScreen && !isLoading && connectionState === "disconnected" && !session.isSessionLive) {
                controller.openWidget();
                void controller.startCall(shell.getSnapshot().callMode);
            }
            else if (shell.isOpen) {
                shell.minimize();
            }
            else {
                controller.openWidget();
            }
        },
        // ─── Call lifecycle ────────────────────────────────────────────────────────
        async startCall(mediaType) {
            shell.setCallMode(mediaType);
            // Fresh conversation for every new call — old transcript/components never
            // carry over.
            resetConversation();
            const result = await session.start({
                agentId: config.agentId,
                variables: config.variables ?? {},
                mediaType,
            });
            if (!result) {
                typing.clear();
                return;
            }
            shell.setCurrentCallId(result.callId);
            slashCommands.setRuntimeCommands(result.slashCommands);
            // In text mode raise the dots immediately: a greeting is always coming,
            // and they cover both connect and first-reply latency.
            if (mediaType === "text")
                typing.showOnUserSend();
            // Re-arm the once-per-conversation "ended" guard, then announce the start
            // (production's dispatchConversationStart, App.tsx:254-257).
            conversationEndedDispatched = false;
            eventBus.emit("ringg:conversation_status", { status: "started", mode: mediaType, callId: result.callId });
        },
        async endCall() {
            const { currentCallId, callMode } = shell.getSnapshot();
            // Guarded like production's conversationEndedDispatched ref: endCall
            // re-enters via onSessionEnded (livekit emits Disconnected synchronously
            // inside room.disconnect()), and the host must see exactly one "ended"
            // per conversation.
            if (currentCallId && !conversationEndedDispatched) {
                conversationEndedDispatched = true;
                eventBus.emit("ringg:conversation_status", { status: "ended", mode: callMode, callId: currentCallId });
            }
            slashCommands.reset();
            typing.clear();
            components.reset();
            await session.end();
            if (config.bypassFeedbackScreen) {
                resetConversation();
                shell.close();
            }
            else {
                shell.showFeedback();
                shell.open();
            }
        },
        // ─── Chat ──────────────────────────────────────────────────────────────────
        async sendMessage(text) {
            messages.addChatMessage({ message: text, isSelf: true });
            typing.showOnUserSend();
            await transport.sendChatMessage(text);
        },
        // ─── Components ────────────────────────────────────────────────────────────
        async sendComponentResponse(componentId, responseData) {
            const pending = components.takePendingResponse(componentId);
            if (!pending)
                return;
            const { method, payload } = formatComponentResponse(pending.name, componentId, responseData, pending.originalConfig);
            await transport.performRpcToAgent(method, payload);
            eventBus.emit("ringg:component_acknowledgement", {
                componentName: pending.name,
                componentId,
                status: "responded",
            });
        },
        async sendBlocksAction(toolId, componentId, action) {
            const { method, payload } = formatBlocksAction(toolId, componentId, action);
            try {
                await transport.performRpcToAgent(method, payload);
            }
            catch {
                // Production console.errors and never surfaces it — resolving anyway
                // lets the BlocksStep spinner clear.
            }
        },
        callComponentApi(method, endpoint, payload) {
            return api.callComponentApi(method, endpoint, payload);
        },
        async sendDynamicData(payload) {
            const { method, payload: rpcPayload } = formatDynamicData(payload);
            await transport.performRpcToAgent(method, rpcPayload);
        },
        addLocalComponent(component) {
            // Direct commit, like production's local widget messages (slash-command
            // widgets) — NOT the chat buffering path: there's no agent text coming
            // to anchor under, and no typing/notification/open side effects apply.
            messages.addComponent(component);
        },
        displaySelection(componentId, label) {
            messages.addLocalSelection(componentId, label);
        },
        markFlowComplete(componentId) {
            components.markFlowComplete(componentId);
            eventBus.emit("ringg:calendar_booking", { status: "confirmed", componentId, message: "Flow completed" });
        },
        registerDynamicDataExtension(extension) {
            return components.registerExtension(extension);
        },
        // ─── Slash commands ────────────────────────────────────────────────────────
        async sendSlashCommand(command) {
            // WIDGET_TOOL commands render locally — extensions (client packs) own
            // them; nothing goes over the wire from core.
            if (components.runExtensions("slash_command", { command: command.command, tool_type: command.tool_type }))
                return;
            const { method, payload } = formatSlashCommand(command.command);
            await transport.performRpcToAgent(method, payload);
        },
        // ─── Feedback ──────────────────────────────────────────────────────────────
        async submitFeedback(rating, comment) {
            const { currentCallId } = shell.getSnapshot();
            if (currentCallId) {
                try {
                    await api.submitFeedback(currentCallId, { rating, comment });
                    eventBus.emit("ringg:feedback_status", { status: "submitted", callId: currentCallId, rating });
                }
                catch {
                    // Feedback is best-effort — closing the widget must never fail on it.
                }
            }
            resetConversation();
            shell.close();
        },
        skipFeedback() {
            const { currentCallId } = shell.getSnapshot();
            if (currentCallId) {
                eventBus.emit("ringg:feedback_status", { status: "skipped", callId: currentCallId });
            }
            resetConversation();
            shell.close();
        },
        // ─── Teardown ──────────────────────────────────────────────────────────────
        destroy() {
            unsubscribers.forEach((unsubscribe) => unsubscribe());
            transport.unregisterRpcMethod("send_dynamic_data");
            transport.unregisterRpcMethod("render_component");
            transport.unregisterRpcMethod("send_source_urls");
            transport.unregisterTextStreamHandler("lk.transcription");
            transport.unregisterTextStreamHandler("ringg.blocks");
            void transport.disconnect();
            [shell, session, messages, typing, components, slashCommands].forEach((store) => store.dispose());
            eventBus.off();
        },
    };
    wireTransport();
    // defaultExpanded seeds the shell store open at creation (no "maximised"
    // event on mount, matching production where isOpen starts true), so no
    // explicit open() call is needed here.
    return controller;
}
